import { useTranslation } from "react-i18next";
import { useRecoilCallback, useRecoilState } from "recoil";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Paper, Stack, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import React, { useCallback, useContext, useEffect } from "react";
import _ from "underscore";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useIsFetching, useMutation, useQuery } from "@tanstack/react-query";
import CustomContentWrapper from "../../../../util/component/CustomContentWrapper";
import CustomLoader from "../../../../util/component/CustomLoader";
import SectionHeader from "../../../../util/component/SectionHeader";
import { dayjsDateTime, LOCALE_DATE_TIME_FORMAT, LOCALE_ISO_DATE_TIME_FORMAT } from "../../utils";
import { Assets ,CarToReplace, EventInfo, ReplacementCar, FirstDriver, BookingOwner, BillingOwner } from "./sections";
import { accordionExpandedState } from "./recoil";
import { validationSchema } from "./form";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { AccordionsExpanded, IFormValues, substitutionReasonValue } from "./types";
import { getBookingSubstitution, saveBookingSubstitution } from "../../api/services";
import { CarGroupType, ReactQueryKey, RouteParams } from "../../types";
import AdditionalDrivers from "./sections/additional-drivers/AdditionalDrivers";
import { HealthAndSafety } from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import { isBusinessContinuityMode } from "../insert/recoil";

const RequestSubstitutionPage = () => {
    const { t } = useTranslation('booking');
    const { dispatchGlobalData } = useContext(GlobalContext);
    const { id } = useParams<RouteParams>();
    const theme = useTheme();
    const navigate = useNavigate();
    const today = dayjsDateTime();
    const { globalData } = useContext(GlobalContext);
    const [businessContinuityMode, setBusinessContinuityMode] = useRecoilState(isBusinessContinuityMode);
    const [queryParameters] = useSearchParams();
    const setAccordionExpanded = useRecoilCallback(({ set }) => (accordionName: keyof AccordionsExpanded, value: boolean) => {
        set(accordionExpandedState(accordionName), value);
    });
    const isFetchingCountries = useIsFetching({
        queryKey: [ReactQueryKey.Countries]
    });
    const isFetchingProvinces = useIsFetching({
        queryKey: [ReactQueryKey.Provinces]
    });
    const recoveryMode = queryParameters.get('recoveryMode') === '1' || false;

    const form = useForm<IFormValues>({
        defaultValues: {
            originRaData: {
                pickupDate: undefined,
                dropoffDate: undefined,
                dropoffLocation: null
            },
            eventInfo: {
                reason: '',
                eventDate: undefined,
                eventPlaceGoogleAddress: '',
                eventPlaceCountry: '',
                eventPlaceProvince: '',
                eventPlaceCity: '',
                eventPlacePostalCode: '',
                eventPlaceStreetName: '',
                eventPlaceStreetNumber: '',
                eventDescription:'',
                vehicleAvailability: true,
                policeInvolved: false,
                notePoliceInvolved: '',
                substancesInfluence: false
            },
            assets: {
                caiModuloDenuncia: null,
                denunciaFurtoSmarrimento: null,
                ricevutaCarroattrezzi: null
            },
            carToReplace: {
                dropoffLocation: null,
                dropoffDate: undefined
            },
            replacementCar: {
                pickupLocation: null,
                pickupDate: undefined,
                dropoffLocation: '',
                dropoffDate: null,
                vehicleType: '',
                carGroup: '',
                ancillaries: [],
                excDamage: 0,
                excFireTheft: 0,
                mileageIncluded: '',
                ekm: ''
            },
            firstDriver: {
                id: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
            },
            bookingOwner: {
                id: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
                companyName: '',
                country: '',
                city: '',
                streetName: '',
                streetNumber: '',
                postalCode: '',
                certifiedEmailAddress: '',
                telephone: '',
                vatNumber: '',
                vatNumberCountry: '',
                legalEntity: null
            },
            billingOwner: {
                id: '',
                type: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                birthNation: '',
                birthPlace: '',
                residenceCountry: '',
                fiscalCode: '',
                drivingLicenseNumber: '',
                email: '',
                mobile: '',
                mobilePrefix: '',
                billingCountry: '',
                billingCity: '',
                billingStreetName: '',
                billingStreetNumber: '',
                billingPostalCode: '',
                certifiedEmailAddress: '',
                vatNumber: '',
                vatNumberCountry: '',
                invoiceCode: '',
                companyName: '',
                legalEntity: null
            },
            additionalDriver: []
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(validationSchema, {}, { rawValues: true }),
        context: {
            dmsAssociatedOperator: globalData.user.mobilityStores.map(
                ((elem:any) => {
                    return elem.sapId;
                })
            ),
            recoveryMode,
        }
    });

    const substitutionQuery = useQuery({
        queryKey: [ReactQueryKey.SubstitutionDataById, id],
        queryFn: () => {
            if (id) {
                return getBookingSubstitution(id)
            }
        },
        refetchOnMount: true,
        onSuccess: async (data) => {
            const raData = data?.raData;
            if (!raData) {
                return;
            }
            form.reset({
                originRaData: {
                    pickupDate: dayjs(raData.pickupDate),
                    dropoffDate: dayjs(raData.dropoffDate),
                    dropoffLocation: raData.dropoffLocation
                },
                eventInfo: {
                    reason: '',
                    eventDate: today,
                    eventPlaceGoogleAddress: '',
                    eventPlaceCountry: '',
                    eventPlaceProvince: '',
                    eventPlaceCity: '',
                    eventPlacePostalCode: '',
                    eventPlaceStreetName: '',
                    eventPlaceStreetNumber: '',
                    eventDescription:'',
                    vehicleAvailability: true,
                    policeInvolved: false,
                    notePoliceInvolved: '',
                    substancesInfluence: false
                },
                carToReplace: {
                    dropoffLocation: raData.dropoffLocation,
                    dropoffDate: today
                },
                assets: {
                    caiModuloDenuncia: null,
                    denunciaFurtoSmarrimento: null,
                    ricevutaCarroattrezzi: null
                },
                replacementCar: {
                    pickupLocation: raData.dropoffLocation,
                    pickupDate: today,
                    dropoffLocation: raData.dropoffLocation.label,
                    dropoffDate: dayjs(raData.dropoffDate),
                    vehicleType:  t(`vehicle_type.${raData.carGroup?.bookedVehicleType}`),
                    carGroup: raData.carGroup.type === CarGroupType.Acriss ? raData.carGroup?.acrissCode : raData.carGroup?.sippCode ?? '',
                    ancillaries: !!raData.ancillaries ? raData.ancillaries
                        .filter((a: any) => a.selected)
                        .map((a: any)=> `${a.units > 1 ? a.units + ' x ' : ''}${a.label}`) : [],
                    excDamage: raData.excDamage ?? 0,
                    excFireTheft: raData.excFireTheft ?? 0,
                    mileageIncluded: !!raData.mileageIncluded ? (raData.mileageIncluded === '0KM' || raData.mileageIncluded === '0' ? 'UNLIMITED' : raData.mileageIncluded) : '',
                    ekm: raData.ekm.toString() ?? '',
                },
                firstDriver: {
                    id: raData.firstDriver?.customerId ?? '',
                    firstName: raData.firstDriver?.name ?? '',
                    lastName: raData.firstDriver?.surname ?? '',
                    birthDate: raData.firstDriver?.birthDate ? dayjs(raData.firstDriver?.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: raData.firstDriver?.birthNation ?? '',
                    birthPlace: raData.firstDriver?.birthPlace ?? '',
                    residenceCountry: raData.firstDriver?.residenceCountry ?? '',
                    fiscalCode: raData.firstDriver?.fiscalCode ?? '',
                    drivingLicenseNumber: raData.firstDriver?.drivingLicenseNumber ?? '',
                    email: raData.firstDriver?.email ?? '',
                    mobile: raData.firstDriver?.mobile ?? '',
                    mobilePrefix: !!raData.firstDriver?.mobilePrefix?.prefix && !!raData.firstDriver?.mobilePrefix?.country ? '+' + raData.firstDriver?.mobilePrefix?.prefix  + ' (' + raData.firstDriver?.mobilePrefix?.country+ ')' : '',
                    webCheckIn: raData.firstDriver?.webCheckIn ? t('label.yes') : t('label.no'),
                    gender: raData.firstDriver?.gender ?? ''
                },
                bookingOwner: {
                    id: raData.bookingOwner?.customerId ? raData.bookingOwner.customerId : raData.bookingCompanyOwner?.customerId ? raData.bookingCompanyOwner.customerId : '',
                    firstName: raData.bookingOwner?.name ?? '',
                    lastName: raData.bookingOwner?.surname ?? '',
                    birthDate: raData.bookingOwner?.birthDate ? dayjs(raData.bookingOwner.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: raData.bookingOwner?.birthNation ?? '',
                    birthPlace: raData.bookingOwner?.birthPlace ?? '',
                    residenceCountry: raData.bookingOwner?.residenceCountry ?? '',
                    fiscalCode: raData.bookingOwner?.fiscalCode ? raData.bookingOwner.fiscalCode : raData.bookingCompanyOwner?.fiscalCode ? raData.bookingCompanyOwner.fiscalCode : '',
                    drivingLicenseNumber: raData.bookingOwner?.drivingLicenseNumber ?? '',
                    email: raData.bookingOwner?.email ? raData.bookingOwner.email : raData.bookingCompanyOwner?.email ? raData.bookingCompanyOwner.email : '',
                    mobile: raData.bookingOwner?.mobile ? raData.bookingOwner.mobile : raData.bookingCompanyOwner?.mobile ? raData.bookingCompanyOwner.mobile : '',
                    mobilePrefix: !!raData.bookingOwner?.mobilePrefix?.prefix && !!raData.bookingOwner?.mobilePrefix?.country ? '+' + raData.bookingOwner?.mobilePrefix?.prefix  + ' (' + raData.bookingOwner?.mobilePrefix?.country + ')' : !!raData.bookingCompanyOwner?.mobilePrefix?.prefix && !!raData.bookingCompanyOwner?.mobilePrefix?.country ? '+' + raData.bookingCompanyOwner?.mobilePrefix?.prefix  + ' (' + raData.bookingCompanyOwner?.mobilePrefix?.country + ')' : '',
                    companyName: raData.bookingCompanyOwner?.companyName ?? '',
                    country: raData.bookingCompanyOwner?.country ?? '',
                    city: raData.bookingCompanyOwner?.city ?? '',
                    streetName: raData.bookingCompanyOwner?.streetName ?? '',
                    streetNumber: raData.bookingCompanyOwner?.streetNumber ?? '',
                    postalCode: raData.bookingCompanyOwner?.postalCode ?? '',
                    certifiedEmailAddress: raData.bookingCompanyOwner?.pec ?? '',
                    telephone: raData.bookingCompanyOwner?.telephone ?? '',
                    sdiCode: raData.bookingCompanyOwner?.sdiCode ?? '',
                    vatNumber: raData.bookingCompanyOwner?.vatNumber ?? '',
                    vatNumberCountry: raData.bookingCompanyOwner?.vatNumberCountry ?? '',
                    legalEntity: !!raData.bookingCompanyOwner?.legalEntity?.label ? t(`legalForms.${raData.bookingCompanyOwner?.legalEntity?.label}`) : null,
                },
                billingOwner: {
                    id: raData.invoicePerson?.customerId ? raData.invoicePerson.customerId : raData.invoiceCompany?.customerId ? raData.invoiceCompany.customerId : '',
                    type: raData.invoicePerson?.type ? raData.invoicePerson.type : raData.invoiceCompany?.type ? raData.invoiceCompany.type : '',
                    firstName: raData.invoicePerson?.name ?? '',
                    lastName: raData.invoicePerson?.surname ?? '',
                    birthDate: raData.invoicePerson?.birthDate ? dayjs(raData.invoicePerson.birthDate).format('DD-MM-YYYY') : '',
                    birthNation: raData.invoicePerson?.birthNation ?? '',
                    birthPlace: raData.invoicePerson?.birthPlace ?? '',
                    residenceCountry: raData.invoicePerson?.residenceCountry ?? '',
                    fiscalCode: raData.invoicePerson?.fiscalCode ? raData.invoicePerson.fiscalCode : raData.invoiceCompany?.fiscalCode ? raData.invoiceCompany.fiscalCode : '',
                    drivingLicenseNumber: raData.invoicePerson?.drivingLicenseNumber ?? '',
                    email: raData.invoicePerson?.email ?  raData.invoicePerson?.email : raData.invoiceCompany?.email ? raData.invoiceCompany.email : '',
                    mobile: raData.invoicePerson?.mobile ?? '',
                    mobilePrefix: !!raData.invoicePerson?.mobilePrefix?.prefix && !!raData.invoicePerson?.mobilePrefix?.country ? '+' + raData.invoicePerson?.mobilePrefix?.prefix  + ' (' + raData.invoicePerson?.mobilePrefix?.country+ ')' : '',
                    billingCountry: raData.invoicePerson?.billingCountry ? raData.invoicePerson.billingCountry : raData.invoiceCompany?.billingCountry ? raData.invoiceCompany.billingCountry : '',
                    billingCity: raData.invoicePerson?.billingCity ? raData.invoicePerson.billingCity : raData.invoiceCompany?.billingCity ? raData.invoiceCompany.billingCity : '',
                    billingStreetName: raData.invoicePerson?.billingStreetName ? raData.invoicePerson.billingStreetName : raData.invoiceCompany?.billingStreetName ? raData.invoiceCompany.billingStreetName : '',
                    billingStreetNumber: raData.invoicePerson?.billingStreetNumber ? raData.invoicePerson.billingStreetNumber : raData.invoiceCompany?.billingStreetNumber ? raData.invoiceCompany.billingStreetNumber : '',
                    billingPostalCode: raData.invoicePerson?.billingPostalCode ? raData.invoicePerson.billingPostalCode : raData.invoiceCompany?.billingPostalCode ? raData.invoiceCompany.billingPostalCode : '',
                    certifiedEmailAddress: raData.invoicePerson?.pec ? raData.invoicePerson.pec : raData.invoiceCompany?.pec ? raData.invoiceCompany.pec : '',
                    vatNumber: raData.invoiceCompany?.vatNumber ? raData.invoiceCompany.vatNumber : '',
                    vatNumberCountry: raData.invoiceCompany?.vatNumberCountry ? raData.invoiceCompany.vatNumberCountry : '',
                    invoiceCode: raData.invoiceCompany?.invoiceCode ? raData.invoiceCompany.invoiceCode : '',
                    companyName: raData.invoiceCompany?.companyName ?? '',
                    billingProvince: raData.invoicePerson?.billingProvince ? raData.invoicePerson.billingProvince : raData.invoiceCompany?.billingProvince ? raData.invoiceCompany.billingProvince : '',
                    legalEntity: !!raData.invoiceCompany?.legalEntity ? t(`legalForms.${raData.invoiceCompany?.legalEntity?.label}`) : null
                },
                additionalDriver: raData.additionalDrivers?.map((ad: any) => ({
                    id: ad.customerId ?? '',
                    firstName: ad.name,
                    lastName: ad.surname,
                    email: ad.email,
                    webCheckIn: ad.webCheckIn ? t('label.yes') : t('label.no')
                }))
            })
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    });

    const handleSubmit = useCallback(async(data: IFormValues) => {
        if(!id) return

        const subData = {
            reason: data.eventInfo.reason ?? '',
            eventDate: data.eventInfo.eventDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) || '',
            eventLocation: {
                eventPlaceCountry: data.eventInfo.eventPlaceCountry ?? '',
                eventPlaceCity: data.eventInfo.eventPlaceCity ?? '',
                eventPlaceProvince: data.eventInfo.eventPlaceProvince ?? '',
                eventPlacePostalCode: data.eventInfo.eventPlacePostalCode ?? '',
                eventPlaceStreetName: data.eventInfo.eventPlaceStreetName ?? '',
                eventPlaceStreetNumber: data.eventInfo.eventPlaceStreetNumber ?? ''
            },
            eventDescription: data.eventInfo.eventDescription ?? '',
            vehicleAvailability: data.eventInfo.vehicleAvailability,
            policeInvolved: data.eventInfo.policeInvolved,
            notePoliceInvolved: data.eventInfo.notePoliceInvolved ?? '',
            substancesInfluence: data.eventInfo.substancesInfluence,
            dropoffDate: data.eventInfo.vehicleAvailability ? data.carToReplace.dropoffDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) || '' : data.eventInfo.eventDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) || '',
            dropoffLocation: data.carToReplace.dropoffLocation
        };
        const bookingData = {
            pickupLocation: data.replacementCar.pickupLocation ?? null,
            pickupDate: data.replacementCar.pickupDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
            dropoffLocation: substitutionQuery?.data?.raData?.dropoffLocation ?? null,
            dropoffDate: data.replacementCar.dropoffDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
            recovery: recoveryMode,
        };
        await execSaveSubstitutionMutation.mutateAsync({
            idRentalAgreement: id,
            subData: JSON.stringify(subData),
            bookingData: JSON.stringify(bookingData),
            caiModuloDenuncia: ([substitutionReasonValue.INCIDENTE_CON_CONTROPARTE, substitutionReasonValue.INCIDENTE_SENZA_CONTROPARTE, substitutionReasonValue.FURTO_PARZIALE, substitutionReasonValue.INCENDIO].includes(data.eventInfo.reason)) ? data.assets?.caiModuloDenuncia : null,
            denunciaFurtoSmarrimento: [substitutionReasonValue.FURTO_VEICOLO, substitutionReasonValue.FURTO_PARZIALE, substitutionReasonValue.TARGA_SMARRITA].includes(data.eventInfo.reason)? data.assets?.denunciaFurtoSmarrimento : null,
            ricevutaCarroattrezzi:  ![substitutionReasonValue.FURTO_VEICOLO].includes(data.eventInfo.reason) ? data?.assets?.ricevutaCarroattrezzi : null
        })
    }, [form.formState, id]);

    const execSaveSubstitutionMutation = useMutation(
        saveBookingSubstitution,
        {
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSuccess: (data) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.success.default'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                navigate(`/booking/view/${data.id}`);
            }
        }
    );

    useEffect(() => {
        if (!form.formState.isSubmitting) {
            if (!_.isEmpty(form.formState.errors)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.form'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });

                console.log('errors', form.formState.errors);
                if (form.formState.errors.eventInfo) {
                    setAccordionExpanded('eventInfo', true);
                }
                if (form.formState.errors.assets) {
                    setAccordionExpanded('assets', true);
                }
                if (form.formState.errors.carToReplace) {
                    setAccordionExpanded('carToReplace', true);
                }
                if (form.formState.errors.replacementCar) {
                    setAccordionExpanded('replacementCar', true);
                }
                return;
            }
        }
    }, [form.formState.isSubmitting, form.formState.errors]);

    //QUESTO USE EFFECT SERVE PER VEDERE SE ALL'INTERNO DELL'URL C'è request-booking-substitution PER POI FAR VISUALIZZARE L'ICONA ROSSA
    // QUESTO COME SU InsertBookingPage.tsx
    useEffect(() => {
            setBusinessContinuityMode(window.location.pathname.includes('request-booking-substitution'));
        }, [window.location]);

    return(
        <>
            <SectionHeader title={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.substitutionRequest')}</Typography>
                        { recoveryMode &&
                             <Chip label={t(`label.recoverySubstitution`)} color={'error'} icon={<HealthAndSafety />}/>
                        }
                    </Stack>
                </Stack>
            }
            subtitle={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.idRentalAgreement')}</Typography>
                        <Typography variant="bodyLarge">{id}</Typography>
                    </Stack>
                </Stack>
            } />
            <CustomContentWrapper>
                <Paper style={{
                    borderRadius: "4px",
                    padding: theme.spacing(1) + ' ' + theme.spacing(2),
                    marginBottom: theme.spacing(2)
                }}
                       elevation={1}
                >
                    <Grid container spacing={2}>
                        <FormProvider {...form}>
                            <Grid item xs={12}>
                                <EventInfo />
                            </Grid>
                            <Grid item xs={12}>
                                <Assets />
                            </Grid>
                            <CarToReplace />
                            <Grid item xs={12}>
                                <ReplacementCar />
                            </Grid>
                            <Grid item xs={12}>
                                <FirstDriver />
                            </Grid>
                            <Grid item xs={12}>
                                <BookingOwner />
                            </Grid>
                            <Grid item xs={12}>
                                <BillingOwner />
                            </Grid>
                            <Grid item xs={12}>
                                <AdditionalDrivers />
                            </Grid>
                        </FormProvider>
                    </Grid>
                </Paper>
            </CustomContentWrapper>
            <Stack direction="row" justifyContent="end" alignItems="center" columnGap={2} p={2}>
                <Button onClick={form.handleSubmit(handleSubmit)} disabled={!substitutionQuery?.data || execSaveSubstitutionMutation.isLoading}>
                    {t('label.save')}
                </Button>
            </Stack>
            <CustomLoader loading={ substitutionQuery.isLoading ||
                                    !!isFetchingCountries ||
                                    !!isFetchingProvinces ||
                                    execSaveSubstitutionMutation.isLoading} />
        </>
    )
}

export default RequestSubstitutionPage;
